import type { QueryClient } from '@tanstack/react-query';
import { availableOrgMembersKeys } from '@/hooks/api/useAvailableOrgMembersForProject';
import { folderKeys } from '@/hooks/api/useFolders';
import { orgProjectBudgetTreeKeys } from '@/hooks/api/useOrgProjectBudgetTree';
import { orgMemberProjectsKeys, orgMembersKeys } from '@/hooks/api/useOrgMembers';
import { projectPermissionGroupKeys } from '@/hooks/api/useProjectPermissionGroups';
import { projectKeys } from '@/hooks/api/useProjects';
import { projectApi } from '@/services/api/projects';
import type { Project } from '@/types/project';
import type { ScreenplayWorkflowSnapshot } from '@/types/ai-session';

export const projectShareKeys = {
  all: ['project-shares'] as const,
  functionalRoles: () => [...projectShareKeys.all, 'functional-roles'] as const,
  shares: (projectId: string) => [...projectShareKeys.all, 'shares', projectId] as const,
  members: (projectId: string) => [...projectShareKeys.all, 'members', projectId] as const,
  publicInfo: (token: string) => [...projectShareKeys.all, 'public-info', token] as const,
  publicAccess: (token: string, password?: string) =>
    [...projectShareKeys.all, 'public-access', token, password ?? null] as const,
};

export type InvalidateProjectMemberRelatedQueriesOptions = {
  /** false 时不刷新项目成员列表；适用于调用方已维护本地成员视图的操作。 */
  refetchMembers?: boolean;
  /** false 时仅标记 detail 过期，不触发活跃 observer 自动 refetch（由 sync 单次拉取写入）。 */
  refetchProjectDetail?: boolean;
  /** false 时不刷新项目列表、项目树等集合查询；适用于只改职能、不改变成员可见范围的操作。 */
  refetchProjectCollections?: boolean;
  refetchSubprojectMemberManagement?: boolean;
};

export function invalidateProjectMemberRelatedQueries(
  queryClient: QueryClient,
  projectId: string,
  options?: InvalidateProjectMemberRelatedQueriesOptions
): void {
  void queryClient.invalidateQueries({
    queryKey: ['screenplay-workflow-snapshot'],
    predicate: (query) =>
      (query.state.data as ScreenplayWorkflowSnapshot | undefined)?.projectId === projectId,
  });
  const refetchMembers = options?.refetchMembers ?? true;
  const refetchProjectDetail = options?.refetchProjectDetail ?? true;
  const refetchProjectCollections = options?.refetchProjectCollections ?? true;
  const refetchSubprojectMemberManagement = options?.refetchSubprojectMemberManagement ?? true;
  if (refetchMembers) {
    queryClient.invalidateQueries({ queryKey: projectShareKeys.members(projectId) });
  }
  queryClient.invalidateQueries({
    queryKey: projectPermissionGroupKeys.byProject(projectId),
  });
  queryClient.invalidateQueries({ queryKey: availableOrgMembersKeys.all });
  queryClient.invalidateQueries({ queryKey: orgMemberProjectsKeys.all });
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return key[0] === 'projects' && key[1] === 'detail' && key.includes(projectId);
    },
    refetchType: refetchProjectDetail ? 'active' : 'none',
  });
  if (refetchSubprojectMemberManagement) {
    queryClient.invalidateQueries({
      queryKey: [...projectKeys.all, 'subproject-member-management'],
    });
  }
  if (refetchProjectCollections) {
    queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
    queryClient.invalidateQueries({ queryKey: orgProjectBudgetTreeKeys.all, exact: false });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }
  queryClient.invalidateQueries({ queryKey: ['org-operation-logs'] });
}

/** 外部成员审核通过后，成员配置读的是父项目成员接口，不能只失效单集成员列表。 */
export function invalidateExternalProjectReviewQueries(
  queryClient: QueryClient,
  input: { orgId: string; projectId: string }
): void {
  invalidateProjectMemberRelatedQueries(queryClient, input.projectId);
  queryClient.invalidateQueries({ queryKey: orgMembersKeys.byOrg(input.orgId) });
}

export type ProjectMemberCacheInvalidationStrategy = 'revoke';

function isProjectDetailQueryKey(key: readonly unknown[], projectIds: string[]): boolean {
  return (
    key[0] === 'projects' &&
    key[1] === 'detail' &&
    projectIds.some((projectId) => key.includes(projectId))
  );
}

export function invalidateProjectMemberRelatedQueriesForProjects(
  queryClient: QueryClient,
  projectIds: string[],
  options?: { strategy?: ProjectMemberCacheInvalidationStrategy }
): void {
  const uniqueProjectIds = [...new Set(projectIds.filter(Boolean))];
  const strategy = options?.strategy ?? 'revoke';
  if (strategy === 'revoke' && uniqueProjectIds.length > 0) {
    queryClient.removeQueries({
      predicate: (query) => isProjectDetailQueryKey(query.queryKey, uniqueProjectIds),
    });
  }
  uniqueProjectIds.forEach((projectId) =>
    invalidateProjectMemberRelatedQueries(queryClient, projectId)
  );
  if (uniqueProjectIds.length === 0) {
    queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    queryClient.invalidateQueries({ queryKey: projectKeys.details() });
    queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }
}

const projectDetailSyncInflight = new Map<string, Promise<void>>();

function isProjectDetailForbiddenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: number; response?: { status?: number } };
  return err.code === 403 || err.response?.status === 403;
}

/**
 * 职能/权限组变更后强制拉取最新项目详情并写入所有 detail 缓存（含 verified 分支），
 * 避免 invalidate + refetch 竞态或列表 initialData 导致按钮 disabled 不刷新。
 */
export async function syncProjectDetailCachesAfterPermissionChange(
  queryClient: QueryClient,
  projectIds: string[]
): Promise<void> {
  const uniqueProjectIds = [...new Set(projectIds.filter(Boolean))];
  if (uniqueProjectIds.length === 0) return;

  await Promise.all(
    uniqueProjectIds.map((projectId) => {
      const inflight = projectDetailSyncInflight.get(projectId);
      if (inflight) return inflight;

      const task = (async () => {
        invalidateProjectMemberRelatedQueries(queryClient, projectId, {
          refetchProjectDetail: false,
        });

        await queryClient.cancelQueries({
          predicate: (query) => isProjectDetailQueryKey(query.queryKey, [projectId]),
        });

        try {
          const freshProject: Project = await projectApi.getById(projectId);
          queryClient.setQueriesData<Project>(
            {
              predicate: (query) => isProjectDetailQueryKey(query.queryKey, [projectId]),
            },
            freshProject
          );
        } catch (error) {
          if (isProjectDetailForbiddenError(error)) {
            console.warn('[project-cache] 跳过无权访问的项目详情同步', { projectId, error });
            queryClient.removeQueries({
              predicate: (query) => isProjectDetailQueryKey(query.queryKey, [projectId]),
            });
            return;
          }
          throw error;
        }
      })().finally(() => {
        projectDetailSyncInflight.delete(projectId);
      });

      projectDetailSyncInflight.set(projectId, task);
      return task;
    })
  );
}
